import { pickBy } from 'lodash';
import { FC, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';

import { uriToPack } from '~src/modules/questions/queryParameters';
import { StoreI } from '~src/rootReducer';
import { defaultPulseCheckApi } from '~src/services/ApiController';
import logger from '~src/services/logger/LlsLogger';
import { circleParts } from '~src/shared/buttons/TimeOutButton';
import { CurrentPack, CycleI, PackName } from '~src/interfaces/cycle.interface';
import RedirectContent from '~src/modules/questions/components/RedirectContent';
import RedirectAction from '~src/modules/questions/components/RedirectAction';
import { isAdmin } from '~src/store/session/session.reducer';
import * as SC from './redirectCard.sc';

export const timerCountDown = 8000 // 8000;
const timeForSlidingIn = 500; // 500;
const timeForSlidingOut = 350; // 350;

interface StateProps {
  unfinishedCycles: Record<string, CycleI>;
  isAdmin: boolean;
}

interface OwnProps {
  onRedirect: (cyclePack?: CyclePack) => void;
}

type Props = OwnProps & StateProps;

export interface CyclePack {
  cycleId: string;
  packAbout: string;
  packName: PackName;
}

const RedirectCard: FC<Props> = (props) => {
  let isUnmounted: boolean = false;

  const location = useLocation();
  const [hideButton, setHideButton] = useState(false);
  const [progress, setProgress] = useState(0);

  const [nextMinutes, setNextMinutes] = useState<number>(0);

  const getNextCycle = (unfinishedCyclePacks: CyclePack[], old: CurrentPack) => {
    const  currentIndex = unfinishedCyclePacks.findLastIndex((unfinished) =>
      old.cycleId === unfinished.cycleId &&
      old.packName === unfinished.packName &&
      old.packAbout === unfinished.packAbout
    );
    // if not found (i.e. -1), assume it's gone from the unfinished cycles & we may just as well pick the next one.
    let newIndex = currentIndex + 1;
    if (unfinishedCyclePacks.length <= newIndex) {
      logger.info('looping around to index 0')
      newIndex = 0;
    }
    return unfinishedCyclePacks[newIndex];
  };

  const unfinishedCyclePacks: CyclePack[] = useMemo(() => {
    return Object.values(props.unfinishedCycles).flatMap((cycle) => {
      return cycle.packs.filter((pack) => !pack.finished).map((pack) => ({
        cycleId: cycle.id,
        packAbout: pack.about?.id,
        packName: pack.name,
      }));
    });
  }, [props.unfinishedCycles]);

  const oldCyclePack: CurrentPack | undefined = useMemo(() => {
    return uriToPack(location.pathname, location.search);
  }, [location.pathname, location.search]);

  const nextPack: CyclePack|undefined = useMemo(() => {
    return oldCyclePack ? getNextCycle(unfinishedCyclePacks, oldCyclePack) : unfinishedCyclePacks[0];
  }, [unfinishedCyclePacks, oldCyclePack])

  useEffect(() => {
    if (nextPack) {
      fetchData(nextPack);
    }
    return () => {
      isUnmounted = true;
    };
  }, [nextPack]);

  const fetchData = (nextPack: CyclePack) => {

    const { cycleId, packName } = nextPack;
    switch (packName) {
      case PackName.SelfAssessment:
      case PackName.Assessment: {
        void defaultPulseCheckApi
          .getQuestions({cycleId, packName})
          .then((d) => {
            if (d.data && !isUnmounted) {
              setNextMinutes(Math.ceil(d.data.length * 0.33));
            }
          });
        break;
      }
      case PackName.SelfSelection: {
        void defaultPulseCheckApi
          .getSelfSelection(cycleId)
          .then((rsp) => {
            if (rsp.data && !isUnmounted) {
              setNextMinutes(1);
            }
          });
        break;
      }
      case PackName.ManagersSelection: {
        void defaultPulseCheckApi
          .getManagersSelection(cycleId)
          .then((rsp) => {
            if (rsp.data && !isUnmounted) {
              setNextMinutes(Math.ceil(rsp.data.length * 0.33));
            }
          });
      }
    }
  };
/*
  const calcInfos = () => {
    // animation progress:
    startAnimation();
    // check if there is a current cycle selected
    // checkForCurrentCycle();
    // whats the next cycle/pack ?
    // props.changeCurrentCycle(getNextCycle());

    try {
      fetchData();
    } catch (err) {
      props.onRedirect();
      logger.info('Error fetching Questions', {
        error: err as Error,
        info: {
          componentStack: 'search.js',
        },
      });
    }
  };
 */
/*
  const checkForCurrentCycle = async () => {
    // if the Redirect comes from link set the cycle from the url
    if (props.currentCycle === null) {
      const query: { lastCycle?: string; index?: string; packIndex?: string, packName?: PackName; packAbout?: string; } = queryString.parse(
        location.search,
      );

      await props.changeCurrentCycle({
        cycleId: query.lastCycle as string,
        packName: query.packName,
        packAbout: query.packAbout,
      });

    }
  };
 */

  const redirectToQS = () => {
    setHideButton(true);
    setProgress(circleParts);
    setTimeout(() => {
      const { unfinishedCycles } = props;
      if (!isUnmounted && unfinishedCycles) {
        setProgress(0);
        props.onRedirect(nextPack)
      }
    }, timeForSlidingOut);
  };

  if (isUnmounted) {
    return <></>
  }

  const nextCycle = nextPack && props.unfinishedCycles[nextPack.cycleId]

  return (
    <SC.RedirectContainer>
      <RedirectContent
        moreQuestions={unfinishedCyclePacks.length > 0}
        isAdmin={props.isAdmin}
        // redirect={hideButton}
        showRedirect={true}
      />
      {nextCycle && (
        <SC.RedirectNavigation
          $redirect={hideButton}
          $timeForSlidingIn={timeForSlidingIn}
          $timeForSlidingOut={timeForSlidingOut}
        >
            <RedirectAction
              hideButton={hideButton}
              redirectToQS={redirectToQS}
              startProgress={true}
              progress={progress}
              nextCycle={nextCycle}
              redirectPack={nextPack.packName}
              nextMinutes={nextMinutes}
            />
          </SC.RedirectNavigation>
      )}
    </SC.RedirectContainer>
  );
}

const mapStateToProps = (state: StoreI): StateProps => ({
  unfinishedCycles: pickBy(state.user.cycles.byId, (cycle) => cycle.packs.some((p) => !p.finished)),
  isAdmin: isAdmin(state),
});

const RedirectCardSuperComponent = connect(mapStateToProps)(RedirectCard);

export default RedirectCardSuperComponent;
